import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import debounce from "lodash/debounce";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import {Close as CloseIcon, DataTable, Search as SearchIcon, SettingsPanel} from "@vedmarket/ui-kit";

import SupportAccordionCard from "../accordion-card";
import {useSupportTickets} from "../../support-hooks";
import Request from "./components/request";
import RequestId from "./components/request-id";
import Settings from "./components/settings";
import Status from "./components/status";
import {
  SUPPORT_REQUESTS_ACTIVE_COLUMNS,
  SUPPORT_REQUESTS_TABLE_COLUMN,
  getColumnName,
} from "./constants";
import './support-requests-list.scss'

type Props = {
  handleSupportTicketsRequest(data?: DictionaryParamsInput): void
  onSearch(query: string): void
}

const SUPPORT_REQUESTS_PER_PAGE = 10;
const SUPPORT_REQUESTS_ROWS_PER_PAGE_OPTIONS = [ 10, 20, 50 ];

const getSupportOrdering = (ordering: string) => {
  const direction = ordering.startsWith('-') ? '-' : '';
  const field = ordering.replace(/^-/, '');

  if (field === SUPPORT_REQUESTS_TABLE_COLUMN.ID) {
    return `${direction}created_at`;
  }

  return ordering;
}

const getTableOrdering = (ordering: string) => (
  ordering.replace('created_at', SUPPORT_REQUESTS_TABLE_COLUMN.ID)
)

const SupportRequestsList = ({handleSupportTicketsRequest, onSearch}: Props) => {

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [ activeColumns, setActiveColumns ] = useState<SUPPORT_REQUESTS_TABLE_COLUMN[]>(SUPPORT_REQUESTS_ACTIVE_COLUMNS);

  const tickets = useSupportTickets();

  const ticketsData = tickets?.data;
  const ticketsParams = tickets?.params;

  const [ searchQuery, setSearchQuery ] = useState(ticketsParams?.search || '');

  const debouncedRequest = useMemo(() => debounce(onSearch, 1000), [ onSearch ]);

  useEffect(() => {
    return () => debouncedRequest.cancel();
  }, [ debouncedRequest ]);

  const handleChangeColumn = (id: SUPPORT_REQUESTS_TABLE_COLUMN) => {
    setActiveColumns((prevColumns) =>
      prevColumns.includes(id)
        ? prevColumns.filter((column) => column !== id)
        : [ ...prevColumns, id ]
    )
  }

  const handleOpenRequest = (id: string) => {
    navigate(`/cabinet/support/${id}`);
  }

  const handleClearSearch = () => {
    setSearchQuery('');
    debouncedRequest.cancel();
    onSearch('');
  }

  const handleSearch = (search: string) => {
    setSearchQuery(search);
    debouncedRequest(search);
  }

  const handleSendRequest = (params: DictionaryParamsInput) => {
    handleSupportTicketsRequest({
      ...params,
      ordering: params.ordering ? getSupportOrdering(params.ordering) : ticketsParams?.ordering,
    });
  }

  const getHeadCells = () => {
    const allColumns = [
      {
        id: SUPPORT_REQUESTS_TABLE_COLUMN.REQUEST,
        numeric: false,
        disablePadding: false,
        label: getColumnName(SUPPORT_REQUESTS_TABLE_COLUMN.REQUEST, t),
        noSort: true,
      },
      {
        id: SUPPORT_REQUESTS_TABLE_COLUMN.STATUS,
        numeric: false,
        disablePadding: false,
        label: getColumnName(SUPPORT_REQUESTS_TABLE_COLUMN.STATUS, t),
        noSort: true,
      },
      {
        id: SUPPORT_REQUESTS_TABLE_COLUMN.ID,
        numeric: false,
        disablePadding: false,
        label: getColumnName(SUPPORT_REQUESTS_TABLE_COLUMN.ID, t),
      },
      {
        id: SUPPORT_REQUESTS_TABLE_COLUMN.SETTINGS,
        numeric: false,
        disablePadding: true,
        label: <SettingsPanel
          handleSelect={handleChangeColumn}
          activeColumns={activeColumns}
          activeColumnsAll={SUPPORT_REQUESTS_ACTIVE_COLUMNS}
          activeColumnsMin={[
            SUPPORT_REQUESTS_TABLE_COLUMN.REQUEST,
            SUPPORT_REQUESTS_TABLE_COLUMN.SETTINGS,
          ]}
          excludedColumnIds={[
            SUPPORT_REQUESTS_TABLE_COLUMN.REQUEST,
            SUPPORT_REQUESTS_TABLE_COLUMN.SETTINGS,
          ]}
          menuId='support-requests-settings-panel'
          selectAllLabel={t('calc__text__packaging__showHideEverything')}
          setActiveColumns={setActiveColumns}
          getLabel={(id: SUPPORT_REQUESTS_TABLE_COLUMN) => getColumnName(id, t)}
        />,
        noSort: true,
      },
    ]

    return allColumns.filter((column) => activeColumns.includes(column.id))
  }

  const preferences = useMemo(() => ([
    { id: SUPPORT_REQUESTS_TABLE_COLUMN.REQUEST, width: 459 },
    { id: SUPPORT_REQUESTS_TABLE_COLUMN.STATUS, width: 120 },
    { id: SUPPORT_REQUESTS_TABLE_COLUMN.ID, width: 69 },
    { id: SUPPORT_REQUESTS_TABLE_COLUMN.SETTINGS, width: 40, noSort: true },
  ]), [])

  const activePreferences = useMemo(
    () => preferences.filter((column) => activeColumns.includes(column.id)),
    [ activeColumns, preferences ]
  )

  const rows = ticketsData?.results.map((request) => ({
    request_id: request.id,
    [SUPPORT_REQUESTS_TABLE_COLUMN.REQUEST]: <Request item={request} onOpen={handleOpenRequest} />,
    [SUPPORT_REQUESTS_TABLE_COLUMN.STATUS]: <Status item={request} />,
    [SUPPORT_REQUESTS_TABLE_COLUMN.ID]: <RequestId item={request} />,
    [SUPPORT_REQUESTS_TABLE_COLUMN.SETTINGS]: <Settings onOpen={() => handleOpenRequest(request.id)} />,
  })) || []

  return (
    <section className='support-requests-list'>
      <SupportAccordionCard title={t('support__text__myRequests')}>
        <div className='support-requests-list__content'>
          <TextField
            fullWidth
            label={t('common__text__search')}
            value={searchQuery}
            onChange={(event) => handleSearch(event.target.value)}
            InputLabelProps={{shrink: true}}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {searchQuery ? (
                    <IconButton
                      aria-label={t('actions__text__clear')}
                      onClick={handleClearSearch}
                      size='small'
                      sx={{
                        cursor: 'pointer',
                        height: '24px',
                        marginRight: 0,
                        padding: 0,
                        width: '24px',
                        '&.MuiIconButton-root': {
                          marginRight: 0,
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            }}
          />
          <div className='support-requests-table'>
            <DataTable
              key={ticketsParams?.search}
              rows={rows}
              count={ticketsData?.count}
              headCells={getHeadCells()}
              preferences={activePreferences}
              defaultOrderBy={getTableOrdering(ticketsParams?.ordering || '-created_at')}
              defaultRowsPerPage={SUPPORT_REQUESTS_PER_PAGE}
              rowsPerPageOptions={SUPPORT_REQUESTS_ROWS_PER_PAGE_OPTIONS}
              primaryField='request_id'
              enableSelection={false}
              handleSendRequest={handleSendRequest}
              showPagination
            />
          </div>
        </div>
      </SupportAccordionCard>
    </section>
  );
};

export default SupportRequestsList;
